import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";

// eslint-disable-next-line
const codeExamples = {
  curl: `curl -X POST http://loghook.net/api/events \\
  -H "Content-Type: application/json" \\
  -d '{
        "token": "unique-user-token",
        "topic": "SystemMonitor",
        "message": "ALIVE"
      }'`,

  python: `import requests
url = 'http://loghook.net/api/events'
data = {
    "token": "unique-user-token",
    "topic": "SystemMonitor",
    "message": "ALIVE"
}
response = requests.post(url, json=data)
print(response.text)`,

  javascript: `const axios = require('axios');
const url = 'http://loghook.net/api/events';
const data = {
    token: 'unique-user-token',
    topic: 'SystemMonitor',
    message: 'ALIVE'
};

axios.post(url, data)
    .then(response => console.log(response.data))
    .catch(error => console.error(error));`,

  c: `#include <stdio.h>
#include <stdlib.h>
#include <curl/curl.h>

int main() {
    CURL *curl;
    CURLcode res;

    curl = curl_easy_init();
    if(curl) {
        // Set the URL
        curl_easy_setopt(curl, CURLOPT_URL, "http://loghook.net/api/events");
        
        // Set the POST fields
        curl_easy_setopt(curl, CURLOPT_POSTFIELDS, 
            "{\\"token\\":\\"unique-user-token\\","
            "\\"topic\\":\\"SystemMonitor\\","
            "\\"message\\":\\"ALIVE\\"}");
        
        // Perform the request and handle the response
        res = curl_easy_perform(curl);
        if(res != CURLE_OK)
            fprintf(stderr, "curl_easy_perform() failed: %s\\n", curl_easy_strerror(res));

        // Clean up curl resources
        curl_easy_cleanup(curl);
    }
    return 0;
}
`,

  golang: `package main
import (
    "bytes"
    "encoding/json"
    "net/http"
    "fmt"
)

func main() {
    url := "http://loghook.net/api/events"
    data := map[string]string{
        "token": "unique-user-token",
        "topic": "SystemMonitor",
        "message": "ALIVE",
    }
    jsonData, _ := json.Marshal(data)
    resp, err := http.Post(url, "application/json", bytes.NewBuffer(jsonData))
    if err != nil {
        fmt.Println("Error:", err)
        return
    }
    defer resp.Body.Close()
    fmt.Println("Response:", resp.Status)
}`,

  java: `
  import java.io.OutputStream;
  import java.net.HttpURLConnection;
  import java.net.URL;

  public class LoghookExample {
    public static void main(String[] args) {
      try {
        // Setup the URL and connection
        URL url = new URL("http://loghook.net/api/events");
        HttpURLConnection conn = (HttpURLConnection) url.openConnection();
        conn.setRequestMethod("POST");
        conn.setRequestProperty("Content-Type", "application/json; utf-8");
        conn.setDoOutput(true);

        // Create the JSON string
        String jsonInputString = "{"
            + "\\"token\\": \\"unique-user-token\\","
            + "\\"topic\\": \\"SystemMonitor\\","
            + "\\"message\\": \\"ALIVE\\""
            + "}";

        // Send the JSON request body
        try (OutputStream os = conn.getOutputStream()) {
            byte[] input = jsonInputString.getBytes("utf-8");
            os.write(input, 0, input.length);
        }

        // Get the response code and print it
        int responseCode = conn.getResponseCode();
        System.out.println("Response Code: " + responseCode);
      } catch (Exception e) {
        e.printStackTrace();
      }
    }
  }`,
};

const languageMap = {
  curl: "bash",
  python: "python",
  javascript: "javascript",
  c: "cpp",
  golang: "go",
  java: "java",
};

function CodeExampleTabs({ token }) {
  const [selectedLanguage, setSelectedLanguage] = useState("curl");

  const handleCopy = () => {
    const codeToCopy = codeExamples[selectedLanguage];
    navigator.clipboard.writeText(codeToCopy).then(() => {
      alert("Code copied to clipboard!");
    });
  };

  return (
    <div className="my-8 w-full px-4 mx-auto max-w-full md:max-w-4xl lg:max-w-5xl xl:max-w-6xl">
      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        {Object.keys(codeExamples).map((lang) => (
          <button
            key={lang}
            onClick={() => setSelectedLanguage(lang)}
            className={`px-4 py-2 rounded-full font-medium transition border ${
              selectedLanguage === lang
                ? "bg-indigo-500 text-white"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
          >
            {lang.charAt(0).toUpperCase() + lang.slice(1)}
          </button>
        ))}
      </div>

      {/* Code Block with Copy Button */}
      <div className="relative bg-gray-900 text-white p-4 rounded-xl overflow-x-auto">
        <div className="absolute right-4 top-4">
          <button
            onClick={handleCopy}
            className="bg-gray-700 text-white px-2 py-1 rounded hover:bg-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faCopy} /> Copy
          </button>
        </div>
        <SyntaxHighlighter
          language={languageMap[selectedLanguage]}
          style={okaidia}
          customStyle={{ fontSize: "0.75rem", padding: "1rem" }}
        >
          {codeExamples[selectedLanguage].replace("unique-user-token", token)}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

export default CodeExampleTabs;
